import { FcOk, FcClock, FcAlarmClock } from "react-icons/fc";
import { Badge, Table } from "rsuite";
import { ApiSchemaAppointmentCheck } from "../../models/api";
import { UtilityDate } from "../../utils";
import { useState, useMemo } from 'react';

const { Column, HeaderCell, Cell } = Table;

interface IProps {
  apiData: ApiSchemaAppointmentCheck[];
  isFetching: boolean;
}

export function AppointmentCheckDataExchangeTable({
  apiData,
  isFetching,
}: IProps) {
  const [sortBy, setSortBy] = useState<keyof ApiSchemaAppointmentCheck | 'totalTime'>('date');
  const [sortAsc, setSortAsc] = useState(true);

  const calculateTotalTime = (checkInTime?: string, checkOutTime?: string) => {
    if (!checkInTime || !checkOutTime) return '-';
    const checkIn = new Date(checkInTime);
    const checkOut = new Date(checkOutTime);
    const diffMs = checkOut.getTime() - checkIn.getTime();
    const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    return { hours: diffHrs, minutes: diffMins, text: `${diffHrs}h ${diffMins}m` };
  };

  const formatMaxHours = (maxHours?: number) => {
    if (maxHours === undefined) return '-';
    const hrs = Math.floor(maxHours);
    const mins = Math.floor((maxHours % 1) * 60);
    return `${hrs}h ${mins}m`;
  };

  const isOverMaxHours = (totalTime: { hours: number, minutes: number } | string, maxHours?: number) => {
    if (typeof totalTime === 'string' || !maxHours) return false;
    return (totalTime.hours + totalTime.minutes / 60) > maxHours;
  };

  const sortedData = useMemo(() => {
    if (!apiData) return [];
    return [...apiData].sort((a, b) => {
      if (sortBy === 'patientName') {
        const aValue = a.patientName || '';
        const bValue = b.patientName || '';
        const compareResult = aValue.localeCompare(bValue);
        return sortAsc ? compareResult : -compareResult;
      }
      if (sortBy === 'date') {
        const aValue = a.date ? new Date(a.date).getTime() : 0;
        const bValue = b.date ? new Date(b.date).getTime() : 0;
        const compareResult = aValue - bValue;
        return sortAsc ? compareResult : -compareResult;
      }
      if (sortBy === 'totalTime') {
        const aTotal = calculateTotalTime(a.checkedInTime, a.checkedOutTime);
        const bTotal = calculateTotalTime(b.checkedInTime, b.checkedOutTime);
        if (typeof aTotal === 'string' || typeof bTotal === 'string') return 0;
        const aValue = aTotal.hours * 60 + aTotal.minutes;
        const bValue = bTotal.hours * 60 + bTotal.minutes;
        const compareResult = aValue - bValue;
        return sortAsc ? compareResult : -compareResult;
      }
      const aValue = a[sortBy];
      const bValue = b[sortBy];
      const compareResult = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      return sortAsc ? compareResult : -compareResult;
    });
  }, [apiData, sortBy, sortAsc]);

  return (
    <div>
      <Table
        autoHeight
        data={sortedData}
        loading={isFetching}
        className=""
        sortColumn={sortBy}
        sortType={sortAsc ? "asc" : "desc"}
        onSortColumn={(sortColumn, sortType) => {
          setSortBy(sortColumn as keyof ApiSchemaAppointmentCheck | 'totalTime');
          setSortAsc(sortType === "asc");
        }}
        renderEmpty={() => (
          <div className="d-flex justify-content-center text-muted align-items-center h-100 text-center p-5">
            <h3 className="fw-light">No Appointment Check Data Available</h3>
          </div>
        )}
      >
        <Column width={200} sortable>
          <HeaderCell className="fw-bold text-black">
            <span title="Patient Name">Patient Name</span>
          </HeaderCell>
          <Cell>
            {(rowData: ApiSchemaAppointmentCheck) => (
              <span className="d-flex align-items-center">
                {rowData.patientName || '-'}
              </span>
            )}
          </Cell>
        </Column>

        {/* New column for isHospitalised */}
        <Column width={120} align="center" sortable>
          <HeaderCell className="fw-bold text-black">
            <span title="Hospitalised">Hospitalised</span>
          </HeaderCell>
          <Cell>
            {(rowData: ApiSchemaAppointmentCheck) => (
              rowData.isHospitalised
                ? <Badge content="Yes" className="bg-success text-white px-2" />
                : <Badge content="No" className="bg-secondary text-white px-2" />
            )}
          </Cell>
        </Column>

        <Column width={140} sortable>
          <HeaderCell className="fw-bold text-black">
            <span title="Date">Date</span>
          </HeaderCell>
          <Cell>
            {(rowData: ApiSchemaAppointmentCheck) => (
              <span className="d-flex align-items-center">
                {rowData.checkedInTime ? UtilityDate.formatHumanFriendlyDate(rowData.checkedInTime) : '-'}
              </span>
            )}
          </Cell>
        </Column>

        <Column width={140} align="center" sortable>
          <HeaderCell>Task Start Time</HeaderCell>
          <Cell>
            {(rowData: ApiSchemaAppointmentCheck) => (
              <span className="d-flex align-items-center">
                <FcClock className="me-2" />
                {rowData.taskStartTime
                  ? UtilityDate.formatTime(rowData.taskStartTime)
                  : '-'}
              </span>
            )}
          </Cell>
        </Column>

        <Column width={140} sortable>
          <HeaderCell className="fw-bold text-black">
            <span title="Check In Time">Check In Time</span>
          </HeaderCell>
          <Cell>
            {(rowData: ApiSchemaAppointmentCheck) => (
              <span className="d-flex align-items-center">
                <FcClock className="me-2" />
                {rowData.checkedInTime ? UtilityDate.formatTime(rowData.checkedInTime) : '-'}
              </span>
            )}
          </Cell>
        </Column>

        <Column width={140} sortable>
          <HeaderCell className="fw-bold text-black">
            <span title="Check Out Time">Check Out Time</span>
          </HeaderCell>
          <Cell>
            {(rowData: ApiSchemaAppointmentCheck) => (
              <span className="d-flex align-items-center">
                <FcClock className="me-2" />
                {rowData.checkedOutTime ? UtilityDate.formatTime(rowData.checkedOutTime) : '-'}
              </span>
            )}
          </Cell>
        </Column>

        <Column width={140} sortable>
          <HeaderCell className="fw-bold text-black">
            <span title="Total Time">Total Time</span>
          </HeaderCell>
          <Cell>
            {(rowData: ApiSchemaAppointmentCheck) => {
              const totalTime = calculateTotalTime(rowData.checkedInTime, rowData.checkedOutTime);
              const isOver = isOverMaxHours(totalTime, rowData.maxHours);
              return (
                <span className={`d-flex align-items-center ${isOver ? 'text-danger' : 'text-success'}`}>
                  <FcAlarmClock className="me-2" />
                  {typeof totalTime === 'string' ? totalTime : totalTime.text}
                </span>
              );
            }}
          </Cell>
        </Column>

        <Column width={140} sortable>
          <HeaderCell className="fw-bold text-black">
            <span title="Max Hours">Max Hours</span>
          </HeaderCell>
          <Cell>
            {(rowData: ApiSchemaAppointmentCheck) => (
              <span className="d-flex align-items-center">
                <FcAlarmClock className="me-2" />
                {formatMaxHours(rowData.maxHours)}
              </span>
            )}
          </Cell>
        </Column>

        <Column flexGrow={1} sortable>
          <HeaderCell className="fw-bold text-black">
            <span title="Last Synced">Last Synced</span>
          </HeaderCell>
          <Cell>
            {(rowData: ApiSchemaAppointmentCheck) =>
              rowData.lastDESyncedTime ? (
                <span className="d-flex align-items-center">
                  <FcOk className="me-2" />
                  {new Date(rowData.lastDESyncedTime).toLocaleString()}
                </span>
              ) : (
                <Badge content="Not Synced" className="p-1 px-2 bg-warning" />
              )
            }
          </Cell>
        </Column>
      </Table>
    </div>
  );
}
